import React from 'react';
import { Typography } from '@one-thd/sui-atomic-components';
import { AxiosError } from 'axios';
import { useConfigService } from '@thd-nucleus/experience-context';
import { track } from '../../../utils/analytics';
import {
  getPaymentModalsVersion,
  scrollToTop,
  getHyphenatedPhoneNumber,
} from '../../../utils/general';
import { setSuccessSetAsDefaultCardAnalytics } from '../../../utils/payments-analytics';
import { resendT2CRequest, b2bPaymentsApi } from '../../../api/b2b/payments';
import { useModal } from '../../../hooks/use-modal';
import { getCardImagePath } from '../../../utils/credit-cards';
import { usePayments } from '../../../hooks/use-payments';
import { Card } from '../../shared/card/card';
import { getLastModifiedDate } from '../../../utils/date';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { Alert } from '../../shared/alert/alert';
import {
  CHANGES_SAVED_MSG,
  INACTIVE_SAVES_MSG,
  ACTIVE_SAVES_MSG,
  DELETED_CARD_MSG,
  DELETED_CITI_CARDS_SUCCESS_MSG1,
  DELETED_CITI_CARDS_SUCCESS_MSG2,
  HD_PASS_SUCCESS_STOP_AUTH_MSG,
} from '../../../constants/response-success-messages';
import {
  ERR_UNABLE_TO_SAVE,
  DELETED_CITI_CARDS_ERROR_MSG1,
  DELETED_CITI_CARDS_ERROR_MSG2,
  ERR_UNABLE_TO_RESEND_INVITE,
  ERR_GENERIC,
} from '../../../constants/response-error-messages';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { T2CPendingAlert } from '../t2c-pending-alert/t2c-pending-alert';
import {
  IResendInviteModalProps,
  ResendInviteModal,
} from '../resend-invite-modal/resend-invite-modal';
import {
  ResendMultiInviteModal,
  IResendMultiInviteModalProps,
} from '../resend-multi-invite-modal/resend-multi-invite-modal';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { CardType } from '../../../constants/payments';
import { T2CMultiPendingAlert } from '../t2c-multi-pending-alert/t2c-multi-pending-alert';
import { getMultiApproverValues } from '../../../utils/multi-approvers';
import { IT2CMultiResendInviteRequest } from '../../../models/b2b/payments';
import { CustomerRole } from '../../../models/common/customer';
import { setResendPendingInvitesAnalytics } from '../../../utils/multi-approvers-analytics';

interface ICreditCardProps {
  card: IPaymentCardCommonData;
  isMobile: boolean;
  index: number;
  setDeletedCreditCardIndex: (index: number) => void;
  onActionSuccess: (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => void;
  onActionFailure: (
    paymentId: string,
    message: string,
    dataTestId?: string,
  ) => void;
  canShowHDWallet?: boolean;
}

export const CreditCard = ({
  card,
  isMobile,
  index,
  setDeletedCreditCardIndex,
  onActionFailure,
  onActionSuccess,
  canShowHDWallet,
}: ICreditCardProps) => {
  const { isB2B, alertMessage, setAlertMessage, t2cMultipleApproverContacts } =
    usePayments();
  const isCardAddedByPurchaser = card.level === 'user';

  const { openModal, setComponentProps, hideModal } = useModal();
  const { role } = useCustomerInfo();
  const {
    onSetActive,
    onSetInactive,
    onViewPurchases,
    onEditCreditCard,
    onDeleteCreditCard,
    onDefaultCreditCardChange,
    onCreditCardRemotePurchases,
    onHomeDepotWallet,
    onRemoveHDWallet,
  } = useMeatballMenuActions();
  const { svocID } = useCustomerInfo();
  const {
    t2cPrimary,
    paymentId,
    isDefault,
    t2cNumber,
    t2cStatus,
    authorizeFlag,
    hdWalletAuthorized,
    isCardExpired,
    cardStatus,
  } = card;
  const isAdmin = role === CustomerRole.ADMIN;
  const isCardInactive = cardStatus?.toLowerCase() === 'inactive';
  const subtleTextColors = isCardInactive ? 'inactive' : 'subtle';
  const primaryTextColors = isCardInactive ? 'inactive' : 'primary';
  const isT2CAuthorized = t2cStatus?.toLowerCase() === 'enrolled';
  const isT2CPending = t2cStatus?.toLowerCase() === 'pending';
  const isPhoneAndT2CAuthorized = authorizeFlag && isT2CAuthorized;
  const isHDWalletAuth = hdWalletAuthorized?.toUpperCase() === 'Y';
  const isHiddenForAdmin = isAdmin && isCardAddedByPurchaser;
  const isExpired = isCardExpired;
  const canShowHdWalletMessage = isHDWalletAuth && canShowHDWallet;

  const multipleApproversAllowedSvocIDs = useConfigService(
    'T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS',
  );
  const isSvocIDAllowedForMultipleAprovers =
    typeof multipleApproversAllowedSvocIDs === 'string' &&
    multipleApproversAllowedSvocIDs.includes(svocID);

  const multipleApproversEnabled =
    !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
    isSvocIDAllowedForMultipleAprovers;

  const isRemoveHDPassEnabled = !!useConfigService('ENABLE_HDPASS_MENU_REMOVE');

  const {
    pendingT2CAuthPhoneNumbers,
    numberOfPendingT2CAuthContacts,
    numberOfT2CAuthContacts,
    showT2CMultiPendingAlert,
  } = getMultiApproverValues(
    t2cMultipleApproverContacts,
    multipleApproversEnabled,
    t2cStatus,
  );

  const handleDefaultChange = () => {
    const onSuccess = () => {
      setSuccessSetAsDefaultCardAnalytics();
      onActionSuccess(
        paymentId,
        CHANGES_SAVED_MSG.message,
        CHANGES_SAVED_MSG.dataTestId,
      );
    };
    const onFailure = () => {
      onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
    };
    onDefaultCreditCardChange(card, onSuccess, onFailure);
  };

  const handleDeleteCard = () => {
    const onSuccess = () => {
      if (card.citiAccountId) {
        const citiSuccessMsg =
          DELETED_CITI_CARDS_SUCCESS_MSG1.message +
          card.citiAccountId +
          DELETED_CITI_CARDS_SUCCESS_MSG2.message;
        onActionSuccess(paymentId, citiSuccessMsg);
      } else {
        onActionSuccess(
          paymentId,
          DELETED_CARD_MSG.message,
          DELETED_CARD_MSG.dataTestId,
        );
      }
    };
    const onFailure = () => {
      if (card.citiAccountId) {
        const citiErrorMsg =
          DELETED_CITI_CARDS_ERROR_MSG1.message +
          card.citiAccountId +
          DELETED_CITI_CARDS_ERROR_MSG2.message;
        onActionFailure(paymentId, citiErrorMsg);
      } else {
        onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
      }
    };
    onDeleteCreditCard(card, onSuccess, onFailure, () => {
      setDeletedCreditCardIndex(index);
    });
  };

  const handleSetActive = () => {
    const onSuccess = () => {
      onActionSuccess(
        paymentId,
        ACTIVE_SAVES_MSG.message,
        ACTIVE_SAVES_MSG.dataTestId,
      );
    };
    const onFailure = () => {
      onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
    };
    onSetActive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
  };

  const handleSetInactive = () => {
    const onSuccess = () => {
      onActionSuccess(
        paymentId,
        INACTIVE_SAVES_MSG.message,
        INACTIVE_SAVES_MSG.dataTestId,
      );
    };
    const onFailure = () => {
      onActionFailure(paymentId, ERR_UNABLE_TO_SAVE.message);
    };
    onSetInactive(card, CardType.CREDIT_CARD, onSuccess, onFailure);
  };
  const handleRemoveHomeDepotWallet = () => {
    const onSuccess = () => {
      onActionSuccess(paymentId, HD_PASS_SUCCESS_STOP_AUTH_MSG.message);
    };
    const onFailure = (error: AxiosError) => {
      scrollToTop();
      track('home-depot-wallet.post-page-error', {
        displayType: 'inline',
        message: error.message,
        code: error.code,
        type: 'error',
        pageSection: 'hd pass: remove HD pass',
      });
      onActionFailure(paymentId, ERR_GENERIC.message);
    };
    onRemoveHDWallet(card, onSuccess, onFailure);
  };

  const b2cMenuItems = [
    {
      value: 'Edit',
      action: () => {
        onEditCreditCard(card);
      },
      dataTestId: 'edit-meatball-action',
    },
    {
      value: 'Set As Default',
      action: handleDefaultChange,
      isHidden: isDefault,
      dataTestId: 'set-default-meatball-action',
    },
    {
      value: 'Delete',
      action: handleDeleteCard,
      dataTestId: 'delete-meatball-action',
    },
  ];
  const b2bMenuItems = [
    {
      value: 'Edit',
      action: () => {
        onEditCreditCard(card);
      },
      isDisabled: !card.actions?.edit,
      isHidden: isHiddenForAdmin,
      dataTestId: 'edit-meatball-action',
    },
    {
      value: 'Set As Default',
      action: handleDefaultChange,
      isHidden: isDefault || isHiddenForAdmin,
      isDisabled: isCardInactive || !card.actions?.setasDefault,
      dataTestId: 'set-default-meatball-action',
    },
    {
      value: 'View Purchases',
      action: () => {
        onViewPurchases(card, CardType.CREDIT_CARD);
      },
      isDisabled: !card.actions?.viewPurchases,
      dataTestId: 'view-purchases-meatball-action',
    },
    {
      value: 'HD Pass',
      action: () => {
        onHomeDepotWallet(card);
      },
      isDisabled: isCardInactive || isExpired,
      isHidden:
        isRemoveHDPassEnabled ||
        !canShowHDWallet ||
        isHDWalletAuth ||
        card.actions?.removeHDWalletAuth ||
        isHiddenForAdmin ||
        !getPaymentModalsVersion(),
      dataTestId: 'hd-pass-meatball-action',
    },
    {
      value: 'Remove HD Pass',
      action: () => {
        handleRemoveHomeDepotWallet();
      },
      isDisabled: isCardInactive || isExpired,
      isHidden:
        !canShowHDWallet ||
        !card.actions?.removeHDWalletAuth ||
        isHiddenForAdmin ||
        !getPaymentModalsVersion(),
      dataTestId: 'remove-hd-pass-meatball-action',
    },
    {
      value: 'Remote Purchases',
      action: () => {
        onCreditCardRemotePurchases(card);
      },
      isDisabled: isCardInactive || isExpired,
      isHidden: isHiddenForAdmin || !isAdmin || !getPaymentModalsVersion(),
      dataTestId: 'remote-purchases-meatball-action',
    },
    {
      value: 'Set Active',
      action: handleSetActive,
      isHidden: isHiddenForAdmin || !card.actions?.setActive || !isCardInactive,
      isDisabled: !card.actions?.setActive,
      dataTestId: 'set-active-meatball-action',
    },
    {
      value: 'Set Inactive',
      action: handleSetInactive,
      isHidden:
        isHiddenForAdmin || (isCardInactive && !isPhoneAndT2CAuthorized),
      isDisabled: isPhoneAndT2CAuthorized || !card.actions?.setInactive,
      dataTestId: 'set-inactive-meatball-action',
    },
    {
      value: 'Delete',
      action: handleDeleteCard,
      isHidden: isHiddenForAdmin,
      dataTestId: 'delete-meatball-action',
    },
  ];

  const onResendInvite = () => {
    if (!t2cNumber) return;
    setComponentProps({ isLoading: true });

    resendT2CRequest({ t2cNumber, t2cPrimary, paymentId })
      .then(() => {
        onActionSuccess(
          paymentId,
          `Text2Confirm invite resent to ${getHyphenatedPhoneNumber(
            t2cNumber,
          )}.`,
        );
      })
      .catch(() => {
        onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const openResendInviteModal = () => {
    if (!t2cNumber) return;

    const props: IResendInviteModalProps = {
      isLoading: false,
      t2cNumber,
      onResendInvite,
    };

    openModal(ResendInviteModal, props, { size: 'sm' });
  };

  const onResendMultiInvites = () => {
    if (!pendingT2CAuthPhoneNumbers.length) return;
    setComponentProps({ isLoading: true });
    const t2cResendInviteRequest: IT2CMultiResendInviteRequest = {
      t2cResendInviteRequest: {
        resendAllPending: true,
        resendInvites: null,
      },
    };

    b2bPaymentsApi
      .multiResendT2CRequest(t2cResendInviteRequest)
      .then(({ data: { success } }) => {
        if (!success) {
          onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
          return;
        }
        setResendPendingInvitesAnalytics(pendingT2CAuthPhoneNumbers.length);
        onActionSuccess(
          paymentId,
          `Text2Confirm invite resent to ${pendingT2CAuthPhoneNumbers
            .map((approverT2cNumber) =>
              getHyphenatedPhoneNumber(approverT2cNumber),
            )
            .join(', ')}.`,
        );
      })
      .catch(() => {
        onActionFailure(paymentId, ERR_UNABLE_TO_RESEND_INVITE.message);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const openResendMultiInvitesModal = () => {
    if (!pendingT2CAuthPhoneNumbers.length) return;

    const props: IResendMultiInviteModalProps = {
      isLoading: false,
      onResendMultiInvites,
      pendingT2CAuthPhoneNumbers,
    };

    openModal(ResendMultiInviteModal, props, { size: 'sm' });
  };

  const isDeleteSuccessMessage =
    alertMessage?.text === DELETED_CARD_MSG.message;

  const mobileCardAlertMessage = isMobile &&
    alertMessage?.paymentId === paymentId &&
    !isDeleteSuccessMessage && (
      <div>
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  return (
    <>
      {mobileCardAlertMessage}
      <Card
        isDefault={isDefault}
        isInactive={isCardInactive}
        defaultLabel="Default Payment Method"
      >
        <div
          className={`sui-flex sui-justify-between sui-align-center ${
            card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'
          } ${isDefault ? 'sui-mt-2' : ''}`}
        >
          <h3
            className={`sui-font-display sui-text-3xl sui-mr-4 sui-truncate ${
              isCardInactive ? 'sui-text-inactive' : 'sui-text-primary'
            }`}
          >
            {card.cardNickName}
          </h3>
          <div>
            <MeatballsMenu
              label="Payment"
              items={isB2B ? b2bMenuItems : b2cMenuItems}
            />
          </div>
        </div>
        <div className="sui-flex sui-flex-wrap sui-mb-6 sui-items-center">
          <img
            width={50}
            height="auto"
            // TODO use sui-grayscale class instead of style prop when stencil fix this class
            style={isCardInactive ? { filter: 'grayscale(1)' } : undefined}
            className="sui-inline-block sui-mr-4"
            src={getCardImagePath(card.cardBrand, card.cardType)}
            alt="Credit card logo"
          />
          <div className="sui-mr-4">
            <Typography variant="body-lg" color={subtleTextColors}>
              ****{card.cardNumberLast4} {card.buyerId}
            </Typography>
          </div>
          {card.cardExpiryMonth && card.cardExpiryYear && (
            <Typography variant="body-lg" color={subtleTextColors}>
              {`Exp ${card.cardExpiryMonth}/${card.cardExpiryYear.slice(-2)}`}
            </Typography>
          )}
        </div>
        {card.citiAccountId && (
          <div className="sui-mr-4">
            <Typography color={subtleTextColors}>
              Citi Account ID {card.citiAccountId}
            </Typography>
          </div>
        )}
        {isExpired && <Alert status="error">This card is expired</Alert>}
        {isCardInactive && (
          <div className="sui-mb-6">
            <Typography weight="bold">Inactive</Typography>
          </div>
        )}
        {(canShowHdWalletMessage || card.authorizeFlag || isT2CAuthorized) && (
          <div className="sui-mb-6">
            {canShowHdWalletMessage && (
              <Typography color={primaryTextColors}>
                HD Pass Authorized
              </Typography>
            )}
            {card.authorizeFlag && (
              <Typography color={primaryTextColors}>
                Phone Authorized
              </Typography>
            )}
            {isT2CAuthorized && (
              <Typography color={primaryTextColors}>
                Text2Confirm Authorized
                {t2cPrimary && (
                  <span className="sui-text-subtle"> - Primary</span>
                )}
              </Typography>
            )}
          </div>
        )}
        {!showT2CMultiPendingAlert && isT2CPending && t2cNumber && (
          <T2CPendingAlert openResendInviteModal={openResendInviteModal} />
        )}
        {showT2CMultiPendingAlert && (
          <T2CMultiPendingAlert
            numberOfPendingT2CAuthContacts={numberOfPendingT2CAuthContacts}
            numberOfContacts={numberOfT2CAuthContacts}
            onClickResend={openResendMultiInvitesModal}
          />
        )}
        {card.addedBy && (
          <div className="sui-overflow-hidden">
            <Typography truncate color={subtleTextColors}>
              Added by <span data-qm-mask="">{card.addedBy}</span>
            </Typography>
          </div>
        )}
        {card.lastModifiedDate && (
          <Typography color={subtleTextColors}>
            Last edited {getLastModifiedDate(card.lastModifiedDate)}
          </Typography>
        )}
      </Card>
    </>
  );
};

CreditCard.displayName = 'CreditCard';
